import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

var AuthContext = createContext(null);

export function AuthProvider({ children }) {
  var [user, setUser] = useState(null);
  var [loading, setLoading] = useState(true);

  var checkAuth = useCallback(function() {
    // STEP 1: Check URL params for Supabase tokens (from Flutter WebView)
    var urlParams = new URLSearchParams(window.location.search);
    var sbAccessToken = urlParams.get('access_token');
    var sbRefreshToken = urlParams.get('refresh_token');

    if (sbAccessToken && sbRefreshToken) {
      // Clean URL immediately (remove tokens from address bar for security)
      var cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      // Exchange Supabase tokens for our app JWT
      api.post('/auth/supabase-token', {
        access_token: sbAccessToken,
        refresh_token: sbRefreshToken
      }).then(function(res) {
        var data = res.data;
        localStorage.setItem('apollo_token', data.access_token);
        localStorage.setItem('apollo_refresh', data.refresh_token);
        setUser({
          _id: data._id, name: data.name, email: data.email, role: data.role,
          language_pref: data.language_pref, group_id: data.group_id,
          region_id: data.region_id, covered_region_ids: data.covered_region_ids
        });
      }).catch(function(err) {
        console.error('Supabase token exchange failed:', err);
        // Fallback: check existing local token
        checkExistingToken();
      }).finally(function() {
        setLoading(false);
      });
      return;
    }

    // STEP 2: Check existing local JWT token
    checkExistingToken();
  }, []);

  function checkExistingToken() {
    var token = localStorage.getItem('apollo_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api.get('/auth/me').then(function(res) {
      setUser(res.data);
    }).catch(function() {
      localStorage.removeItem('apollo_token');
      localStorage.removeItem('apollo_refresh');
      setUser(false);
    }).finally(function() {
      setLoading(false);
    });
  }

  useEffect(function() { checkAuth(); }, [checkAuth]);

  var login = function(email, password) {
    return api.post('/auth/login', { email: email, password: password }).then(function(res) {
      var data = res.data;
      localStorage.setItem('apollo_token', data.access_token);
      localStorage.setItem('apollo_refresh', data.refresh_token);
      var userData = {
        _id: data._id, name: data.name, email: data.email, role: data.role,
        language_pref: data.language_pref, group_id: data.group_id,
        region_id: data.region_id, covered_region_ids: data.covered_region_ids
      };
      setUser(userData);
      return userData;
    });
  };

  var logout = function() {
    return api.post('/auth/logout').catch(function() {}).then(function() {
      localStorage.removeItem('apollo_token');
      localStorage.removeItem('apollo_refresh');
      setUser(false);
    });
  };

  return React.createElement(AuthContext.Provider, {
    value: { user: user, loading: loading, login: login, logout: logout, setUser: setUser, checkAuth: checkAuth }
  }, children);
}

export function useAuth() {
  var ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
